<template>
  <ContextColorProvider :context="context">
    <LoaderIndicator
      v-if="isLoading"
      class="background"
      data-testid="loader"
      with-container
    />
    <div v-else class="background">
      <NavBar class="nav">
        <span slot="center">
          <BrandImage
            v-if="!isWhiteLabel"
            class="logo"
            data-testid="logo"
            :image="EBrandImageLogos.WARREN_PRIMARY"
            width="104px"
            :schema="EBrandImageSchemas.POSITIVE"
            as="span"
          />
        </span>
      </NavBar>
      <div class="content">
        <div v-if="needNextStep" class="activated">
          <InitialAvatarIcon
            class="email-verified__success"
            icon="EF0070"
            size="medium"
          />
          <p class="text-h-1 title">
            <strong>Falta só mais uma etapa!</strong>
          </p>
          <p class="text-p-2 subtitle">
            Visando a segurança no processo, enviamos um email de confirmação
            para o novo endereço ({{ nextEmail }}).
          </p>
        </div>
        <div v-else-if="isVerified" class="activated">
          <InitialAvatarIcon
            class="email-verified__success"
            icon="EF0070"
            size="medium"
          />
          <p class="text-h-1 title">
            <strong>Seu endereço de email está confirmado</strong>
          </p>
          <p class="text-p-2 subtitle">Tudo pronto para você investir.</p>
          <ButtonPrimary :label="redirectButtonLabel" @click="redirectUser" />
        </div>
        <div v-else class="activation-error">
          <InitialAvatarIcon
            class="email-verified__attention"
            icon="EF0060"
            size="medium"
          />
          <p class="text-h-1 title">
            <strong>Ops! Link temporário inválido.</strong>
          </p>
          <p class="text-p-2 subtitle">
            Valide sua conta reenviando o e-mail de confirmação.
          </p>
          <ButtonPrimary :label="redirectButtonLabel" @click="redirectUser" />
        </div>
      </div>
    </div>
  </ContextColorProvider>
</template>

<script lang="ts">
import { Component, Vue } from 'vue-property-decorator';

import { WarrenSession } from '@/modules/common/services/session';

import {
  ContextTypes,
  NebraskaColors
} from '@warrenbrasil/nebraska-tokens-web';
import {
  NavBar,
  ButtonPrimary,
  ContextColorProvider,
  BrandImage,
  EBrandImageSchemas,
  EBrandImageLogos,
  InitialAvatarIcon,
  LoaderIndicator
} from '@warrenbrasil/nebraska-web';

import { CustomerModule } from '@/modules/common/store/CustomerModule';
import {
  verifyEmail,
  getThemeByParam
} from '@/modules/common/services/http/account';
import { setNebraskaScheme } from '@/modules/common/services/theme';

@Component({
  components: {
    LoaderIndicator,
    NavBar,
    ButtonPrimary,
    ContextColorProvider,
    BrandImage,
    InitialAvatarIcon
  }
})
export default class EmailVerifiedView extends Vue {
  private isLoading = true;
  private isVerified = false;
  private needNextStep = false;
  private isUserLogged = WarrenSession.get('warren.accounts.accessToken');
  private nextEmail = '';
  private NebraskaColors = NebraskaColors;
  private isWhiteLabel = false;
  private brandDomain = '';
  private context = ContextTypes.BRAND;

  readonly EBrandImageLogos = EBrandImageLogos;
  readonly EBrandImageSchemas = EBrandImageSchemas;

  private async mounted() {
    await this.setTheme();
    this.verifyEmail();
  }

  private async setTheme() {
    if (!this.isWarrenBrand()) {
      this.isWhiteLabel = true;

      try {
        const theme = await getThemeByParam('domain', this.brandDomain);
        setNebraskaScheme(theme);
      } catch {
        this.context = ContextTypes.NEUTRAL;
      }
    }
  }

  private isWarrenBrand() {
    this.brandDomain = (this.$route.query.brand as string) ?? 'warrenbrasil';
    return this.brandDomain === 'warrenbrasil';
  }

  private verifyEmail() {
    const routeQuery = this.$route.query;
    const verificationToken = this.$route.params.hash;

    verifyEmail({
      hash: verificationToken,
      step: String(routeQuery.step)
    }).then(
      response => {
        if (response.nextEmail) {
          this.needNextStep = true;
          this.nextEmail = response.nextEmail;
        } else if (response.accessToken) {
          WarrenSession.set(
            'warren.accounts.accessToken',
            response.accessToken
          );

          if (response.user) {
            this.isUserLogged = true;

            CustomerModule.setCustomer = response.user;
          }
        }

        this.isLoading = false;
        this.isVerified = true;
      },
      _error => {
        this.isLoading = false;
      }
    );
  }

  private redirectUser() {
    if (this.isUserLogged) {
      this.$router.push({ path: '/home' });
    } else if (this.brandDomain) {
      this.$router.push({
        path: '/signin',
        query: { brand: this.brandDomain }
      });
    } else {
      this.$router.push({ path: '/signin' });
    }
  }

  get redirectButtonLabel() {
    return this.isUserLogged ? 'Ir para Conta' : 'Ir para Login';
  }
}
</script>

<style lang="less" scoped>
.background {
  height: 100vh;
  min-height: 704px;
  background-color: @background-primary;

  .nav {
    width: 100%;
    justify-self: flex-start;
  }

  .custom-nav-bar {
    background-color: transparent !important;
  }

  .content {
    height: calc(100% - 64px);
    min-height: 640px;

    > div {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      color: @text-negative;
      height: 100%;

      .icon {
        margin-bottom: @size-spacing-x800;
      }

      .title {
        color: @text-primary;
        margin-bottom: @size-spacing-x350;
      }

      .subtitle {
        color: @text-placeholder;
        margin-bottom: @size-spacing-x600;
        text-align: center;
      }
    }
  }
}
.email-verified__success {
  background: @status-positive-background;
  color: @status-positive;
}
.email-verified__attention {
  background: @status-negative-background;
  color: @status-negative;
}
</style>
